import { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, TextField, InputAdornment, Typography } from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';

const SearchableSelect = ({ label, value, onChange, onBlur, options, disabled, error, helperText, required, icon: Icon }) => {
    const [search, setSearch] = useState('');

    const inputRef = useRef(null);

    const filtered = useMemo(() => {
        if (!options) return [];
        const searchTerm = search.trim().toLowerCase();
        if (!searchTerm) return options;
        return options.filter((o) => o.name?.toLowerCase().includes(searchTerm));
    }, [options, search]);

    const handleOpen = () => {
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 50);
    };

    const selectedItem = useMemo(() => options?.find((o) => o.id === value), [options, value]);

    const renderedOptions = useMemo(() => {
        if (!options) return [];
        const top100 = filtered.slice(0, 100);

        // If there's a selected item and it's NOT in the top 100 filtered results,
        // we must still render it so MUI can display its name in the select box.
        if (selectedItem && !top100.some((o) => o.id === selectedItem.id)) {
            return [selectedItem, ...top100];
        }
        return top100;
    }, [filtered, selectedItem, options]);

    return (
        <FormControl fullWidth error={error} disabled={disabled}>
            <InputLabel required={required}>{label}</InputLabel>
            <Select
                value={value}
                label={label}
                onChange={onChange}
                onBlur={onBlur}
                onOpen={handleOpen}
                onClose={() => setSearch('')}
                startAdornment={
                    Icon ? (
                        <InputAdornment position="start">
                            <Icon fontSize="small" color={error ? 'error' : 'action'} />
                        </InputAdornment>
                    ) : undefined
                }
                MenuProps={{
                    autoFocus: false,
                    PaperProps: { style: { maxHeight: 300 } }
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, borderRadius: 2 }}
            >
                {/* Search Box Header */}
                <MenuItem
                    disableRipple
                    disabled
                    sx={{
                        bgcolor: 'background.paper !important',
                        cursor: 'default',
                        opacity: '1 !important',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        pt: 1.5,
                        pb: 1,
                        px: 2
                    }}
                >
                    <TextField
                        size="small"
                        placeholder={`Search ${label}...`}
                        fullWidth
                        inputRef={inputRef}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== 'Escape') {
                                e.stopPropagation();
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                </MenuItem>

                {renderedOptions.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                            No results found
                        </Typography>
                    </MenuItem>
                ) : (
                    renderedOptions.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                        </MenuItem>
                    ))
                )}
                {filtered.length > 100 && (
                    <MenuItem disabled sx={{ bgcolor: 'action.hover', opacity: '1 !important' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Showing first 100 of {filtered.length} matches. Please refine your search.
                        </Typography>
                    </MenuItem>
                )}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

SearchableSelect.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })).isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    required: PropTypes.bool,
    icon: PropTypes.elementType
};

SearchableSelect.defaultProps = {
    onBlur: undefined,
    disabled: false,
    error: false,
    helperText: '',
    required: false,
    icon: undefined
};

export default SearchableSelect;
